// AccountsPage_Redesigned.jsx
// ─── Premium Trading Journal · Accounts Dashboard ──────────────────────────
// Design direction: TradingView × Stripe × Linear — professional prop-firm UX
// Redesigned by: Senior SaaS Product Designer
// ────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from 'react';
import { api, tradesApi } from '../utils/api.js';

/* ─── Page-scoped design tokens ─── */
const PAGE_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,300&display=swap');

  /* ── Font assignments ── */
  .ap-root * { box-sizing: border-box; }
  .ap-root, .ap-root button, .ap-root input, .ap-root textarea, .ap-root select {
    font-family: 'DM Sans', sans-serif;
  }
  .ap-mono { font-family: 'IBM Plex Mono', monospace !important; }

  /* ── Page entrance ── */
  @keyframes ap-fade-up {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes ap-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes ap-card-in {
    from { opacity: 0; transform: translateY(8px) scale(0.99); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes ap-overlay-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes ap-slide-up {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes ap-slide-right {
    from { opacity: 0; transform: translateX(28px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes ap-pulse-gold {
    0%, 100% { box-shadow: 0 0 0 0 rgba(212,168,67,.35); }
    50%       { box-shadow: 0 0 0 6px rgba(212,168,67,0); }
  }
  @keyframes ap-pulse-gold-bg {
    0%, 100% { background: rgba(212,168,67,.08); }
    50%       { background: rgba(212,168,67,.14); }
  }
  @keyframes ap-shimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(200%); }
  }
  @keyframes ap-spin {
    to { transform: rotate(360deg); }
  }
  @keyframes ap-equity-draw {
    from { stroke-dashoffset: 300; }
    to   { stroke-dashoffset: 0; }
  }
  @keyframes ap-progress-fill {
    from { width: 0; }
  }
  @keyframes ap-number-pop {
    0%   { transform: scale(0.92); opacity: 0; }
    60%  { transform: scale(1.04); }
    100% { transform: scale(1);    opacity: 1; }
  }
  @keyframes ap-gold-glow-pulse {
    0%, 100% { box-shadow: 0 0 0 1px rgba(212,168,67,.22), 0 8px 32px rgba(212,168,67,.08), 0 0 0 0 rgba(212,168,67,.15); }
    50%       { box-shadow: 0 0 0 1px rgba(212,168,67,.38), 0 12px 40px rgba(212,168,67,.16), 0 0 28px 4px rgba(212,168,67,.08); }
  }
  @keyframes ap-activity-in {
    from { opacity: 0; transform: translateX(-8px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes ap-live-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: .5; transform: scale(.8); }
  }

  /* ── Card interactions ── */
  .ap-account-card {
    transition: border-color .22s, box-shadow .22s, transform .18s;
    animation: ap-card-in .24s ease both;
  }
  .ap-account-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 48px rgba(0,0,0,.48) !important;
  }
  .ap-account-card--active {
    animation: ap-gold-glow-pulse 3.5s ease infinite !important;
  }
  .ap-account-card--active:hover {
    transform: translateY(-3px);
    box-shadow: 0 0 0 1.5px rgba(212,168,67,.5), 0 20px 56px rgba(212,168,67,.18) !important;
    animation: none !important;
  }

  /* ── KPI card hover ── */
  .ap-kpi-card {
    transition: border-color .18s, box-shadow .18s, transform .16s, background .18s;
  }
  .ap-kpi-card:hover {
    transform: translateY(-2px);
    background: rgba(255,255,255,.04) !important;
  }

  /* ── Buttons ── */
  .ap-btn { transition: all .15s; cursor: pointer; }
  .ap-btn:active { transform: scale(0.975); }

  /* ── Metric stat number ── */
  .ap-stat-num { animation: ap-number-pop .35s cubic-bezier(.34,1.56,.64,1) both; }

  /* ── Equity curve SVG animation ── */
  .ap-equity-line {
    stroke-dasharray: 300;
    stroke-dashoffset: 300;
    animation: ap-equity-draw 1.2s cubic-bezier(.4,0,.2,1) .4s forwards;
  }

  /* ── Progress bar ── */
  .ap-progress-fill {
    animation: ap-progress-fill .9s cubic-bezier(.4,0,.2,1) .5s both;
  }

  /* ── Activity row ── */
  .ap-activity-row {
    transition: background .15s;
  }
  .ap-activity-row:hover {
    background: rgba(255,255,255,.03) !important;
  }

  /* ── Comparison row ── */
  .ap-compare-row {
    transition: background .15s;
  }
  .ap-compare-row:hover {
    background: rgba(255,255,255,.025) !important;
  }

  /* ── Scrollbar ── */
  .ap-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
  .ap-scroll::-webkit-scrollbar-track { background: transparent; }
  .ap-scroll::-webkit-scrollbar-thumb { background: rgba(212,168,67,.18); border-radius: 2px; }
  .ap-scroll::-webkit-scrollbar-thumb:hover { background: rgba(212,168,67,.32); }

  /* ── Active account badge animation ── */
  .ap-active-badge {
    animation: ap-pulse-gold-bg 2.8s ease infinite;
  }
  .ap-live-dot {
    animation: ap-live-dot 1.8s ease infinite;
  }

  /* ── Page layout: main content + accounts rail ── */
  .ap-page-grid {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 22px;
    align-items: start;
  }
  .ap-rail {
    position: sticky;
    top: 20px;
  }
  @media (max-width: 1000px) {
    .ap-page-grid { grid-template-columns: 1fr !important; }
    .ap-rail { position: static; order: 2; }
  }

  /* ── Rail item ── */
  .ap-rail-item { transition: border-color .15s, background .15s; }
  .ap-rail-item:hover { border-color: rgba(212,168,67,.22) !important; }
  .ap-rail-actions { opacity: 0; transition: opacity .15s; }
  .ap-rail-item:hover .ap-rail-actions { opacity: 1; }

  /* ── Responsive ── */
  @media (max-width: 900px) {
    .ap-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .ap-insight-row { grid-template-columns: 1fr !important; }
    .ap-bottom-row { grid-template-columns: 1fr !important; }
  }
  @media (max-width: 640px) {
    .ap-accounts-grid { grid-template-columns: 1fr !important; }
    .ap-card-stats { grid-template-columns: repeat(3, 1fr) !important; }
    .ap-bottom-row { grid-template-columns: 1fr !important; }
  }
  @media (max-width: 420px) {
    .ap-kpi-grid { grid-template-columns: 1fr !important; }
  }
`;

/* ─── Constants ─── */
const ACCOUNT_TYPES = ['Live', 'Demo', 'Prop Firm', 'Paper'];

const ACCOUNT_TYPE_META = {
  'Live':      { color:'var(--green)',          bg:'rgba(46,204,138,.09)',  border:'rgba(46,204,138,.22)', icon:'L', glow:'rgba(46,204,138,.14)', label:'LIVE' },
  'Demo':      { color:'var(--blue)',            bg:'rgba(91,143,232,.09)', border:'rgba(91,143,232,.22)', icon:'D', glow:'rgba(91,143,232,.12)', label:'DEMO' },
  'Prop Firm': { color:'var(--gold)',            bg:'rgba(212,168,67,.09)', border:'rgba(212,168,67,.22)', icon:'P', glow:'rgba(212,168,67,.12)', label:'PROP' },
  'Paper':     { color:'rgba(168,159,140,.75)', bg:'rgba(168,159,140,.07)',border:'rgba(168,159,140,.18)',icon:'T', glow:'rgba(168,159,140,.08)', label:'SIM'  },
};

const PROP_FIRMS = ['FTMO', 'MyForexFunds', 'The5%ers', 'FundedNext', 'Topstep', 'Apex Trader Funding', 'Other'];

/* ─── Helpers ─── */
function generateId() { return Math.random().toString(36).slice(2, 10); }

function validateAccount({ name, balance }) {
  const errors = {};
  if (!name?.trim()) errors.name = 'Account name is required.';
  if (balance !== '' && balance !== undefined && (isNaN(parseFloat(balance)) || parseFloat(balance) < 0)) {
    errors.balance = 'Must be a positive number.';
  }
  return errors;
}

function fmt(n, opts = {}) {
  const num = parseFloat(n);
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', { style:'currency', currency:'USD', maximumFractionDigits:0, ...opts }).format(num);
}

function fmtShort(n) {
  const num = parseFloat(n);
  if (isNaN(num) || num === 0) return '—';
  if (Math.abs(num) >= 1000000) return `$${(num/1000000).toFixed(1)}M`;
  if (Math.abs(num) >= 1000)    return `$${(num/1000).toFixed(1)}K`;
  return fmt(num);
}

function useWindowWidth() {
  const [w, setW] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

/* ─── Micro equity curve SVG ─── */
function EquityCurve({ color = '#D4A843', width = 120, height = 38, points }) {
  // Use real cumulative P&L points if provided, otherwise show flat line
  const pts = (points && points.length >= 2) ? points : [0, 0];
  const max = Math.max(...pts);
  const min = Math.min(...pts);
  const range = max - min || 1;
  const pad = 3;
  const W = width, H = height;
  const xs = pts.map((_, i) => pad + (i / (pts.length - 1)) * (W - pad * 2));
  const ys = pts.map(v => H - pad - ((v - min) / range) * (H - pad * 2));
  const line = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const fill = `${line} L${xs[xs.length-1].toFixed(1)},${H} L${xs[0].toFixed(1)},${H} Z`;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display:'block', overflow:'visible' }}>
      <defs>
        <linearGradient id="eq-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
        <filter id="eq-glow">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <path d={fill} fill="url(#eq-grad)" />
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="ap-equity-line" filter="url(#eq-glow)" />
    </svg>
  );
}

/* ─── Weekly progress bar — Enhanced v2 ─── */
function WeeklyProgress({ current = 0, target = 100, label }) {
  const pct = Math.min(100, Math.max(0, target > 0 ? (current / target) * 100 : 0));
  const remaining = Math.max(0, target - current);
  const color = pct >= 100 ? 'var(--green)' : pct >= 60 ? 'var(--gold)' : pct >= 30 ? 'rgba(91,143,232,.9)' : 'rgba(168,159,140,.55)';
  const trackColor = pct >= 100 ? 'rgba(46,204,138,.12)' : pct >= 60 ? 'rgba(212,168,67,.1)' : 'rgba(255,255,255,.05)';

  // Milestone markers at 25%, 50%, 75%
  const milestones = [25, 50, 75];

  return (
    <div style={{ width:'100%' }}>
      {/* Top row: label + completion badge */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <span style={{ fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'rgba(168,159,140,.55)' }}>
          {label || 'Weekly Target'}
        </span>
        <span style={{
          fontSize:10, fontWeight:700, color,
          background: pct >= 100 ? 'rgba(46,204,138,.1)' : 'transparent',
          border: pct >= 100 ? '1px solid rgba(46,204,138,.2)' : 'none',
          padding: pct >= 100 ? '1px 8px' : '0',
          borderRadius:20, fontFamily:"'IBM Plex Mono',monospace",
        }}>
          {pct >= 100 ? '✓ GOAL HIT' : `${pct.toFixed(0)}%`}
        </span>
      </div>

      {/* Dollar progress line */}
      <div style={{ display:'flex', alignItems:'baseline', gap:6, marginBottom:9 }}>
        <span className="ap-mono" style={{ fontSize:16, fontWeight:600, color, letterSpacing:'-.01em' }}>
          {fmtShort(current)}
        </span>
        <span style={{ fontSize:11, color:'rgba(168,159,140,.35)' }}>/</span>
        <span className="ap-mono" style={{ fontSize:12, fontWeight:500, color:'rgba(168,159,140,.55)' }}>
          {fmtShort(target)} goal
        </span>
        {remaining > 0 && (
          <span style={{ fontSize:11, color:'rgba(168,159,140,.38)', marginLeft:'auto' }}>
            {fmtShort(remaining)} remaining
          </span>
        )}
      </div>

      {/* Progress track */}
      <div style={{ position:'relative', height:6, background:trackColor, borderRadius:3, overflow:'visible' }}>
        {/* Milestone markers */}
        {milestones.map(m => (
          <div key={m} style={{
            position:'absolute', left:`${m}%`, top:-2, width:1, height:10,
            background: m <= pct ? 'rgba(255,255,255,.15)' : 'rgba(255,255,255,.07)',
            zIndex:2,
          }} />
        ))}
        {/* Fill */}
        <div
          className="ap-progress-fill"
          style={{
            height:'100%', width:`${pct}%`, background:`linear-gradient(90deg,${color}cc,${color})`,
            borderRadius:3, position:'relative',
            boxShadow: pct > 5 ? `0 0 8px ${color}66` : 'none',
          }}
        >
          {/* Tip glow */}
          {pct > 3 && pct < 100 && (
            <div style={{ position:'absolute', right:0, top:'50%', transform:'translateY(-50%)', width:8, height:8, borderRadius:'50%', background:color, boxShadow:`0 0 6px 2px ${color}88` }} />
          )}
        </div>
      </div>

      {/* Milestone labels */}
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
        {milestones.map(m => (
          <span key={m} style={{ fontSize:9, color: m <= pct ? 'rgba(168,159,140,.45)' : 'rgba(168,159,140,.22)', fontFamily:"'IBM Plex Mono',monospace", position:'relative', left: m === 25 ? '-4px' : m === 75 ? '4px' : '0' }}>
            {m}%
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── KPI stat card — Premium v2 ─── */
function KpiCard({ label, value, sub, delta, deltaPositive, deltaLabel, icon, accent, delay = 0 }) {
  const deltaColor = deltaPositive === true ? 'var(--green)' : deltaPositive === false ? 'var(--red)' : 'rgba(168,159,140,.7)';
  const deltaBg    = deltaPositive === true ? 'rgba(46,204,138,.09)' : deltaPositive === false ? 'rgba(224,85,85,.09)' : 'rgba(168,159,140,.07)';
  const deltaArrow = deltaPositive === true ? (
    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="18 15 12 9 6 15"/></svg>
  ) : deltaPositive === false ? (
    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"/></svg>
  ) : null;

  return (
    <div className="ap-kpi-card" style={{
      background:'rgba(255,255,255,.025)',
      border:'1px solid rgba(255,255,255,.07)',
      borderRadius:11,
      padding:'16px 17px 14px',
      position:'relative',
      overflow:'hidden',
      animation:`ap-fade-up .3s ease ${delay}ms both`,
      cursor:'default',
    }}>
      {accent && <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,transparent 0%,${accent} 40%,${accent} 60%,transparent 100%)`, opacity:.75 }} />}
      {accent && <div style={{ position:'absolute', top:-20, left:'50%', transform:'translateX(-50%)', width:80, height:40, background:accent, opacity:.04, borderRadius:'50%', filter:'blur(18px)', pointerEvents:'none' }} />}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:11 }}>
        <span style={{ fontSize:10, fontWeight:600, letterSpacing:'.06em', textTransform:'uppercase', color:'rgba(168,159,140,.5)' }}>{label}</span>
        {icon && <span style={{ fontSize:13, opacity:.55 }}>{icon}</span>}
      </div>
      <div className="ap-mono ap-stat-num" style={{ fontSize:24, fontWeight:600, color:'var(--white)', lineHeight:1, letterSpacing:'-.025em', marginBottom:10, animationDelay:`${delay + 80}ms` }}>
        {value}
      </div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:6, flexWrap:'wrap' }}>
        {sub && <span style={{ fontSize:11, color:'rgba(168,159,140,.45)', lineHeight:1.3 }}>{sub}</span>}
        {delta && (
          <span style={{
            display:'inline-flex', alignItems:'center', gap:3,
            fontSize:10, fontWeight:700, color:deltaColor,
            background:deltaBg,
            border:`1px solid ${deltaPositive === true ? 'rgba(46,204,138,.18)' : deltaPositive === false ? 'rgba(224,85,85,.18)' : 'rgba(168,159,140,.12)'}`,
            padding:'2px 6px 2px 5px', borderRadius:20,
            fontFamily:"'IBM Plex Mono',monospace", letterSpacing:'.02em',
            flexShrink:0,
          }}>
            {deltaArrow}{delta}
            {deltaLabel && <span style={{ fontWeight:500, opacity:.7, marginLeft:2 }}>{deltaLabel}</span>}
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Toast ─── */
function Toast({ msg, type = 'success' }) {
  const colors = type === 'success'
    ? { bg:'rgba(46,204,138,.1)', border:'rgba(46,204,138,.28)', color:'var(--green)' }
    : { bg:'rgba(224,85,85,.1)',  border:'rgba(224,85,85,.28)',  color:'var(--red)'   };
  return (
    <div style={{
      position:'fixed', bottom:24, right:20, zIndex:9999,
      padding:'11px 16px', background:colors.bg, border:`1px solid ${colors.border}`,
      color:colors.color, fontSize:12, fontWeight:600, borderRadius:9,
      display:'flex', alignItems:'center', gap:8, animation:'ap-fade-up .22s ease',
      boxShadow:'0 10px 36px rgba(0,0,0,.55)', maxWidth:320,
    }}>
      <span style={{ width:16, height:16, borderRadius:'50%', background:colors.border, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9 }}>
        {type === 'success' ? '✓' : '✕'}
      </span>
      {msg}
    </div>
  );
}

/* ─── Confirm modal ─── */
function ConfirmModal({ title, body, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.78)', backdropFilter:'blur(6px)', zIndex:10000, display:'flex', alignItems:'flex-end', justifyContent:'center', animation:'ap-overlay-in .15s ease' }}>
      <div style={{ background:'var(--dark2,#141412)', border:'1px solid rgba(255,255,255,.1)', borderRadius:'14px 14px 0 0', padding:'28px 24px 40px', width:'100%', maxWidth:480, animation:'ap-slide-up .22s ease', boxShadow:'0 -16px 56px rgba(0,0,0,.6)' }}>
        <div style={{ width:36, height:4, background:'rgba(255,255,255,.1)', borderRadius:2, margin:'0 auto 24px' }} />
        <div style={{ fontSize:15, fontWeight:700, color:'var(--white)', marginBottom:8 }}>{title}</div>
        <div style={{ fontSize:13, color:'rgba(200,192,168,.55)', lineHeight:1.65, marginBottom:28 }}>{body}</div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onCancel} className="ap-btn"
            style={{ flex:1, padding:'12px', background:'transparent', border:'1px solid rgba(255,255,255,.1)', color:'rgba(240,234,214,.5)', fontSize:13, fontWeight:600, cursor:'pointer', borderRadius:8, minHeight:42, transition:'all .15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.2)'; e.currentTarget.style.color='rgba(240,234,214,.8)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.1)'; e.currentTarget.style.color='rgba(240,234,214,.5)'; }}>
            Cancel
          </button>
          <button onClick={onConfirm} className="ap-btn"
            style={{ flex:1, padding:'12px', background:danger?'rgba(224,85,85,.12)':'rgba(212,168,67,.12)', border:`1px solid ${danger?'rgba(224,85,85,.4)':'rgba(212,168,67,.4)'}`, color:danger?'var(--red)':'var(--gold)', fontSize:13, fontWeight:700, cursor:'pointer', borderRadius:8, minHeight:42, transition:'all .15s' }}
            onMouseEnter={e => e.currentTarget.style.filter='brightness(1.12)'}
            onMouseLeave={e => e.currentTarget.style.filter='none'}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Field label ─── */
function FieldLabel({ children }) {
  return (
    <label style={{ display:'block', fontSize:10, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'rgba(168,159,140,.55)', marginBottom:6 }}>
      {children}
    </label>
  );
}

/* ─── Text input ─── */
function TInput({ value, onChange, placeholder, type = 'text', prefix, suffix, readOnly, error, autoFocus }) {
  const [focused, setFocused] = useState(false);
  const border = error ? 'rgba(224,85,85,.55)' : focused ? 'rgba(212,168,67,.45)' : readOnly ? 'rgba(255,255,255,.04)' : 'rgba(255,255,255,.09)';
  return (
    <div>
      <div style={{ position:'relative' }}>
        {prefix && <span style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'rgba(168,159,140,.4)', fontSize:13, pointerEvents:'none', zIndex:1 }}>{prefix}</span>}
        <input
          type={type} value={value} onChange={onChange} placeholder={placeholder}
          readOnly={readOnly} autoFocus={autoFocus}
          onFocus={() => !readOnly && setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width:'100%',
            padding:`10px ${suffix?'34px':'13px'} 10px ${prefix?'26px':'13px'}`,
            background: readOnly ? 'rgba(255,255,255,.02)' : 'rgba(255,255,255,.04)',
            border:`1px solid ${border}`,
            color: readOnly ? 'rgba(240,234,214,.25)' : 'var(--white,#F7F3EA)',
            fontSize:13, outline:'none', borderRadius:7,
            transition:'border-color .15s, background .15s',
            minHeight:42,
          }}
        />
        {suffix && <span style={{ position:'absolute', right:11, top:'50%', transform:'translateY(-50%)', color:'rgba(168,159,140,.4)', fontSize:13, pointerEvents:'none' }}>{suffix}</span>}
      </div>
      {error && <div style={{ fontSize:11, color:'var(--red,#E05555)', marginTop:4 }}>{error}</div>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   ACCOUNT FORM DRAWER
   Side panel on desktop · Bottom sheet on mobile
════════════════════════════════════════════════════════════ */
function AccountDrawer({ acct, onSave, onCancel, isMobile }) {
  const isEdit = !!acct?.id;
  const [form, setForm] = useState({
    name:      acct?.name      || '',
    type:      acct?.type      || 'Live',
    broker:    acct?.broker    || '',
    propFirm:  acct?.propFirm  || '',
    balance:   acct?.balance   || '',
    dailyPct:  acct?.dailyPct  || '',
    weekPct:   acct?.weekPct   || '',
    maxTrades: acct?.maxTrades || '',
    notes:     acct?.notes     || '',
  });
  const [errors, setErrors] = useState({});
  const [showCustomPropFirm, setShowCustomPropFirm] = useState(
    () => !!(acct?.propFirm) && !PROP_FIRMS.includes(acct.propFirm)
  );
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const meta = ACCOUNT_TYPE_META[form.type] || ACCOUNT_TYPE_META['Live'];
  const balanceNum = parseFloat(form.balance) || 0;

  const handleSave = () => {
    const errs = validateAccount(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    onSave({ ...form, id: acct?.id || generateId() });
  };

  const inner = (
    <div style={{ padding: isMobile ? '18px 20px 36px' : '28px 28px 36px' }}>
      {isMobile && <div style={{ width:36, height:4, background:'rgba(255,255,255,.1)', borderRadius:2, margin:'0 auto 20px' }} />}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:'var(--white)', letterSpacing:'-.01em' }}>
            {isEdit ? 'Edit Account' : 'Add Trading Account'}
          </div>
          <div style={{ fontSize:11, color:'rgba(168,159,140,.4)', marginTop:2 }}>
            {isEdit ? 'Update details and risk settings' : 'Connect a new account to start journaling'}
          </div>
        </div>
        <button onClick={onCancel} className="ap-btn"
          style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.08)', color:'rgba(168,159,140,.5)', width:30, height:30, borderRadius:7, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
          onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,.1)'; e.currentTarget.style.color='var(--white)'; }}
          onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,.05)'; e.currentTarget.style.color='rgba(168,159,140,.5)'; }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      {/* Account type chips */}
      <div style={{ marginBottom:20 }}>
        <FieldLabel>Account Type</FieldLabel>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:7 }}>
          {ACCOUNT_TYPES.map(t => {
            const m = ACCOUNT_TYPE_META[t];
            const sel = form.type === t;
            return (
              <button key={t} onClick={() => { setForm(p => ({ ...p, type:t, propFirm: t !== 'Prop Firm' ? '' : p.propFirm })); if (t !== 'Prop Firm') setShowCustomPropFirm(false); }} className="ap-btn"
                style={{ padding:'10px 4px', textAlign:'center', background:sel?m.bg:'rgba(255,255,255,.03)', border:`1px solid ${sel?m.border:'rgba(255,255,255,.06)'}`, color:sel?m.color:'rgba(168,159,140,.45)', fontSize:11, fontWeight:sel?700:500, cursor:'pointer', borderRadius:8, letterSpacing:'.02em' }}>
                <div style={{ fontSize:9, fontWeight:800, letterSpacing:'.1em', opacity:.75, marginBottom:3, fontFamily:"'IBM Plex Mono',monospace" }}>{m.label}</div>
                {t}
              </button>
            );
          })}
        </div>
      </div>

      {/* Name / Broker — Broker is dropped for Prop Firm accounts since the
          prop firm itself is the counterparty, not a separate broker. */}
      <div style={{ display:'grid', gridTemplateColumns: (isMobile || form.type === 'Prop Firm') ? '1fr' : '1fr 1fr', gap:12, marginBottom:12 }}>
        <div>
          <FieldLabel>Account Name *</FieldLabel>
          <TInput value={form.name} onChange={set('name')} placeholder="e.g. FTMO Challenge" error={errors.name} autoFocus />
        </div>
        {form.type !== 'Prop Firm' && (
          <div>
            <FieldLabel>Broker</FieldLabel>
            <TInput value={form.broker} onChange={set('broker')} placeholder="e.g. IC Markets" />
          </div>
        )}
      </div>

      {form.type === 'Prop Firm' && (
        <div style={{ marginBottom:12 }}>
          <FieldLabel>Prop Firm</FieldLabel>
          <select
            value={showCustomPropFirm ? 'Other' : form.propFirm}
            onChange={e => {
              const v = e.target.value;
              if (v === 'Other') { setShowCustomPropFirm(true); setForm(p => ({ ...p, propFirm:'' })); }
              else { setShowCustomPropFirm(false); setForm(p => ({ ...p, propFirm:v })); }
            }}
            style={{ width:'100%', padding:'10px 13px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.09)', color: (showCustomPropFirm || form.propFirm) ? 'var(--white)' : 'rgba(168,159,140,.4)', fontSize:13, borderRadius:7, outline:'none', cursor:'pointer' }}>
            <option value="">Select firm…</option>
            {PROP_FIRMS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          {showCustomPropFirm && (
            <div style={{ marginTop:8 }}>
              <TInput value={form.propFirm} onChange={set('propFirm')} placeholder="Type your prop firm's name…" autoFocus />
            </div>
          )}
        </div>
      )}

      {/* Balance */}
      <div style={{ marginBottom:16 }}>
        <FieldLabel>Starting Balance</FieldLabel>
        <TInput type="number" value={form.balance} onChange={set('balance')} placeholder="100,000" prefix="$" error={errors.balance} />
      </div>

      {/* Risk params */}
      <div style={{ background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.05)', borderRadius:9, padding:'14px', marginBottom:16 }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'rgba(168,159,140,.45)', marginBottom:12 }}>Risk Parameters</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
          <div>
            <FieldLabel>Daily Limit</FieldLabel>
            <TInput type="number" value={form.dailyPct} onChange={set('dailyPct')} placeholder="5" suffix="%" />
            {balanceNum > 0 && form.dailyPct && <div style={{ fontSize:10, color:'var(--red)', marginTop:3, fontFamily:"'IBM Plex Mono',monospace", fontWeight:600 }}>${(balanceNum*parseFloat(form.dailyPct)/100).toFixed(0)}</div>}
          </div>
          <div>
            <FieldLabel>Weekly Target</FieldLabel>
            <TInput type="number" value={form.weekPct} onChange={set('weekPct')} placeholder="10" suffix="%" />
            {balanceNum > 0 && form.weekPct && <div style={{ fontSize:10, color:'var(--green)', marginTop:3, fontFamily:"'IBM Plex Mono',monospace", fontWeight:600 }}>${(balanceNum*parseFloat(form.weekPct)/100).toFixed(0)}</div>}
          </div>
          <div>
            <FieldLabel>Max Trades/Day</FieldLabel>
            <TInput type="number" value={form.maxTrades} onChange={set('maxTrades')} placeholder="∞" />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div style={{ marginBottom:22 }}>
        <FieldLabel>Notes</FieldLabel>
        <textarea value={form.notes} onChange={set('notes')} placeholder="Phase 1 — 10% target, 5% max drawdown" rows={2}
          style={{ width:'100%', padding:'10px 13px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.09)', color:'var(--white)', fontSize:13, borderRadius:7, resize:'vertical', lineHeight:1.6, outline:'none' }} />
      </div>

      {/* Actions */}
      <div style={{ display:'flex', gap:9 }}>
        <button onClick={onCancel} className="ap-btn"
          style={{ flex:1, padding:'12px', background:'transparent', border:'1px solid rgba(255,255,255,.09)', color:'rgba(240,234,214,.45)', fontSize:13, fontWeight:600, cursor:'pointer', borderRadius:8, minHeight:42, transition:'all .15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.18)'; e.currentTarget.style.color='rgba(240,234,214,.75)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.09)'; e.currentTarget.style.color='rgba(240,234,214,.45)'; }}>
          Cancel
        </button>
        <button onClick={handleSave} className="ap-btn"
          style={{ flex:2, padding:'12px', background:'linear-gradient(135deg,var(--gold),var(--gold-dim))', border:'none', color:'var(--black)', fontSize:13, fontWeight:700, cursor:'pointer', borderRadius:8, minHeight:42, letterSpacing:'.03em', transition:'filter .15s', position:'relative', overflow:'hidden' }}
          onMouseEnter={e => e.currentTarget.style.filter='brightness(1.09)'}
          onMouseLeave={e => e.currentTarget.style.filter='none'}>
          <span style={{ position:'absolute', inset:0, background:'linear-gradient(90deg,transparent 20%,rgba(255,255,255,.18) 50%,transparent 80%)', animation:'ap-shimmer 2.8s ease infinite', pointerEvents:'none' }} />
          {isEdit ? '✓ Save Changes' : '+ Add Account'}
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.82)', backdropFilter:'blur(7px)', zIndex:10000, display:'flex', alignItems:'flex-end', justifyContent:'center', animation:'ap-overlay-in .15s ease' }}>
        <div style={{ background:'var(--dark2,#141412)', border:'1px solid rgba(255,255,255,.09)', borderRadius:'16px 16px 0 0', width:'100%', maxWidth:580, maxHeight:'93vh', overflowY:'auto', animation:'ap-slide-up .22s ease', boxShadow:'0 -16px 60px rgba(0,0,0,.65)' }}>
          {inner}
        </div>
      </div>
    );
  }

  return (
    <>
      <div onClick={onCancel} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.52)', backdropFilter:'blur(5px)', zIndex:9998, animation:'ap-overlay-in .18s ease' }} />
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:460, background:'var(--dark,#0E0E0C)', borderLeft:'1px solid rgba(255,255,255,.07)', zIndex:9999, overflowY:'auto', animation:'ap-slide-right .22s ease', boxShadow:'-24px 0 72px rgba(0,0,0,.55)' }}>
        {inner}
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   ACCOUNT CARD — Redesigned compact layout
════════════════════════════════════════════════════════════ */
function AccountCard({ acct, isActive, onSetActive, onEdit, onDelete, isMobile, weekPnl = null }) {
  const meta = ACCOUNT_TYPE_META[acct.type] || ACCOUNT_TYPE_META['Live'];
  const balance = parseFloat(acct.balance) || 0;
  const weekTarget = acct.weekPct ? (balance * parseFloat(acct.weekPct) / 100) : 0;
  // weekPnl comes from real API data passed by parent

  return (
    <div
      className={`ap-account-card${isActive ? ' ap-account-card--active' : ''}`}
      style={{
        background: isActive
          ? 'linear-gradient(145deg, rgba(212,168,67,.065) 0%, rgba(14,14,12,1) 55%)'
          : 'rgba(255,255,255,.02)',
        border: isActive
          ? '1.5px solid rgba(212,168,67,.38)'
          : '1px solid rgba(255,255,255,.06)',
        borderRadius: 13,
        padding: '0',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top accent line — thicker + brighter for active */}
      <div style={{ height: isActive ? 2 : 1.5, background: isActive ? `linear-gradient(90deg, transparent 0%, ${meta.color} 30%, ${meta.color} 70%, transparent 100%)` : `linear-gradient(90deg, transparent, ${meta.color}44, transparent)`, borderRadius:'13px 13px 0 0' }} />

      {/* Active: premium corner ornament */}
      {isActive && (
        <div style={{ position:'absolute', top:8, right:8, width:60, height:60, background:'radial-gradient(circle at top right, rgba(212,168,67,.1) 0%, transparent 65%)', pointerEvents:'none', borderRadius:'0 12px 0 0' }} />
      )}

      {/* Card body */}
      <div style={{ padding:'16px 18px 14px' }}>

        {/* Header row */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10, marginBottom:14 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0, flex:1 }}>
            {/* Type monogram */}
            <div style={{
              width: 36, height: 36, flexShrink: 0,
              background: meta.bg,
              border: `1px solid ${meta.border}`,
              borderRadius: 9,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily:"'IBM Plex Mono',monospace", fontSize: 11, fontWeight: 600, color: meta.color,
              letterSpacing:'.02em',
              boxShadow: isActive ? `0 4px 14px ${meta.glow}` : 'none',
            }}>
              {meta.label}
            </div>
            <div style={{ minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:2 }}>
                <div style={{ fontSize:14, fontWeight:600, color:'var(--white)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', letterSpacing:'-.01em' }}>
                  {acct.name}
                </div>
                {isActive && (
                  <div style={{ display:'flex', alignItems:'center', gap:4, padding:'2px 8px 2px 6px', background:'rgba(212,168,67,.12)', border:'1px solid rgba(212,168,67,.28)', borderRadius:20, flexShrink:0 }} className="ap-active-badge">
                    <span style={{ width:4, height:4, borderRadius:'50%', background:'var(--gold)', boxShadow:'0 0 4px var(--gold)' }} className="ap-live-dot" />
                    <span style={{ fontSize:9, fontWeight:800, color:'var(--gold)', letterSpacing:'.08em', fontFamily:"'IBM Plex Mono',monospace" }}>ACTIVE</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize:11, color:'rgba(168,159,140,.45)', display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
                {acct.broker && <span>{acct.broker}</span>}
                {acct.propFirm && <><span style={{ opacity:.35 }}>·</span><span style={{ color: meta.color, opacity:.8 }}>{acct.propFirm}</span></>}
                {!acct.broker && !acct.propFirm && <span style={{ fontStyle:'italic', opacity:.6 }}>No broker set</span>}
              </div>
            </div>
          </div>

          {/* Action buttons — right side */}
          <div style={{ display:'flex', gap:5, flexShrink:0 }}>
            <button onClick={() => onEdit(acct)} className="ap-btn"
              style={{ width:28, height:28, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', color:'rgba(168,159,140,.5)', borderRadius:6, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all .15s' }}
              title="Edit account"
              onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(212,168,67,.35)'; e.currentTarget.style.color='var(--gold)'; e.currentTarget.style.background='rgba(212,168,67,.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.08)'; e.currentTarget.style.color='rgba(168,159,140,.5)'; e.currentTarget.style.background='rgba(255,255,255,.04)'; }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
            </button>
            <button onClick={() => onDelete(acct.id)} className="ap-btn"
              style={{ width:28, height:28, background:'transparent', border:'1px solid rgba(224,85,85,.12)', color:'rgba(224,85,85,.35)', borderRadius:6, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all .15s' }}
              title="Delete account"
              onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(224,85,85,.45)'; e.currentTarget.style.color='var(--red)'; e.currentTarget.style.background='rgba(224,85,85,.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(224,85,85,.12)'; e.currentTarget.style.color='rgba(224,85,85,.35)'; e.currentTarget.style.background='transparent'; }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
            </button>
          </div>
        </div>

        {/* Balance — dominant metric */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'rgba(168,159,140,.45)', marginBottom:4 }}>Balance</div>
          <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
            <div className="ap-mono" style={{ fontSize:26, fontWeight:600, color: isActive ? 'var(--gold)' : 'var(--white)', letterSpacing:'-.02em', lineHeight:1 }}>
              {balance > 0 ? fmtShort(balance) : '—'}
            </div>
            {weekPnl > 0 && (
              <div style={{ fontSize:11, fontWeight:600, color:'var(--green)', display:'flex', alignItems:'center', gap:3, fontFamily:"'IBM Plex Mono',monospace" }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                +{fmtShort(weekPnl)} wk
              </div>
            )}
          </div>
        </div>

        {/* Stats grid — compact 3-col */}
        <div className="ap-card-stats" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginBottom:14 }}>
          {[
            { label:'Daily Limit', value: acct.dailyPct ? `${acct.dailyPct}%` : '—', color:'var(--red)', mono:true },
            { label:'Wk Target',   value: acct.weekPct  ? `${acct.weekPct}%`  : '—', color:'var(--green)', mono:true },
            { label:'Max Trades',  value: acct.maxTrades || '∞',                      color:'rgba(168,159,140,.75)', mono:true },
          ].map(s => (
            <div key={s.label} style={{ background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.05)', borderRadius:7, padding:'8px 10px' }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'rgba(168,159,140,.4)', marginBottom:5 }}>{s.label}</div>
              <div className={s.mono ? 'ap-mono' : ''} style={{ fontSize:13, fontWeight:600, color: s.value === '—' || s.value === '∞' ? 'rgba(168,159,140,.3)' : s.color, lineHeight:1 }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Weekly target progress */}
        {acct.weekPct && balance > 0 && (
          <div style={{ marginBottom:14 }}>
            <WeeklyProgress
              current={weekPnl}
              target={weekTarget}
              label={`Weekly · $${weekTarget.toFixed(0)} target`}
            />
          </div>
        )}

        {/* Equity sparkline — shown only when real points available via weekPnl */}
        {balance > 0 && weekPnl !== null && (
          <div style={{ marginBottom:14 }}>
            <EquityCurve color={isActive ? 'var(--gold)' : meta.color} width={isMobile ? 260 : 300} height={36} points={[0, weekPnl]} />
          </div>
        )}

        {/* Notes */}
        {acct.notes && (
          <div style={{ fontSize:11, color:'rgba(168,159,140,.38)', marginBottom:12, lineHeight:1.6, borderLeft:`2px solid ${meta.border}`, paddingLeft:10, fontStyle:'italic' }}>
            {acct.notes.length > 90 ? acct.notes.slice(0, 90) + '…' : acct.notes}
          </div>
        )}

        {/* Set active CTA */}
        {!isActive ? (
          <button onClick={() => onSetActive(acct.id)} className="ap-btn"
            style={{ width:'100%', padding:'9px 14px', fontSize:11, fontWeight:700, background:'linear-gradient(135deg,var(--gold),var(--gold-dim))', border:'none', color:'var(--black)', cursor:'pointer', borderRadius:7, letterSpacing:'.05em', minHeight:34, display:'flex', alignItems:'center', justifyContent:'center', gap:6, boxShadow:'0 4px 16px rgba(212,168,67,.22)', position:'relative', overflow:'hidden', transition:'filter .15s' }}
            onMouseEnter={e => e.currentTarget.style.filter='brightness(1.09)'}
            onMouseLeave={e => e.currentTarget.style.filter='none'}>
            <span style={{ position:'absolute', inset:0, background:'linear-gradient(90deg,transparent 20%,rgba(255,255,255,.15) 50%,transparent 80%)', animation:'ap-shimmer 3s ease infinite', pointerEvents:'none' }} />
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            Set as Active
          </button>
        ) : (
          <div style={{ width:'100%', padding:'9px 14px', fontSize:11, fontWeight:600, background:'rgba(212,168,67,.07)', border:'1px solid rgba(212,168,67,.18)', color:'rgba(212,168,67,.7)', borderRadius:7, minHeight:34, display:'flex', alignItems:'center', justifyContent:'center', gap:6, letterSpacing:'.04em' }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            Currently Active
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Recent Activity Section ─── */
function RecentActivity({ trades: rawTrades = [] }) {
  // Format relative time from ISO date string
  function relTime(dateStr) {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return 'Just now';
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d === 1) return 'Yesterday';
    if (d < 7) return `${d}d ago`;
    return new Date(dateStr).toLocaleDateString('en-US', { month:'short', day:'numeric' });
  }

  const trades = rawTrades.slice(0, 5).map(t => ({
    pair: t.pair || t.symbol || '—',
    pnl:  parseFloat(t.result ?? t.pnl ?? 0),
    dir:  (t.dir || t.direction || 'long').toLowerCase(),
    time: relTime(t.closedAt || t.date || t.tradeDate),
    icon: (t.dir || t.direction || 'long').toLowerCase() === 'long' ? '↗' : '↘',
  }));

  const entries = []; // Journal entries require a separate API — show nothing until implemented

  return (
    <div style={{ background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.06)', borderRadius:11, overflow:'hidden', animation:'ap-fade-up .4s ease .35s both' }}>
      {/* Header */}
      <div style={{ padding:'13px 16px 11px', borderBottom:'1px solid rgba(255,255,255,.05)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:'var(--green)', boxShadow:'0 0 6px var(--green)', flexShrink:0 }} className="ap-live-dot" />
          <span style={{ fontSize:10, fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color:'rgba(168,159,140,.55)' }}>Recent Trades</span>
        </div>
        <span style={{ fontSize:10, color:'rgba(168,159,140,.3)', fontFamily:"'IBM Plex Mono',monospace" }}>last 7d</span>
      </div>

      {/* Trade rows */}
      <div>
        {trades.length === 0 ? (
          <div style={{ padding:'22px 16px', textAlign:'center', color:'rgba(168,159,140,.35)', fontSize:12 }}>
            No trades logged yet
          </div>
        ) : trades.map((t, i) => {
          const isPos = t.pnl >= 0;
          return (
            <div key={i} className="ap-activity-row" style={{
              display:'flex', alignItems:'center', gap:10, padding:'9px 16px',
              borderBottom: i < trades.length - 1 ? '1px solid rgba(255,255,255,.03)' : 'none',
              animation:`ap-activity-in .3s ease ${i * 50 + 400}ms both`,
              background:'transparent',
            }}>
              {/* Direction indicator */}
              <div style={{
                width:26, height:26, borderRadius:6, flexShrink:0,
                background: isPos ? 'rgba(46,204,138,.09)' : 'rgba(224,85,85,.09)',
                border: `1px solid ${isPos ? 'rgba(46,204,138,.18)' : 'rgba(224,85,85,.18)'}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:11, color: isPos ? 'var(--green)' : 'var(--red)', fontWeight:700,
              }}>
                {t.icon}
              </div>
              {/* Pair */}
              <span className="ap-mono" style={{ fontSize:12, fontWeight:600, color:'var(--white)', letterSpacing:'.02em', flex:1 }}>{t.pair}</span>
              {/* Time */}
              <span style={{ fontSize:10, color:'rgba(168,159,140,.35)' }}>{t.time}</span>
              {/* P&L */}
              <span className="ap-mono" style={{
                fontSize:12, fontWeight:700, minWidth:52, textAlign:'right',
                color: isPos ? 'var(--green)' : 'var(--red)',
              }}>
                {isPos ? '+' : ''}{fmt(t.pnl)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Journal entries — hidden until API integration */}
      {entries.length > 0 && (
      <div style={{ padding:'10px 16px 8px', borderTop:'1px solid rgba(255,255,255,.04)', background:'rgba(255,255,255,.01)' }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color:'rgba(168,159,140,.38)', marginBottom:7 }}>Journal</div>
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          {entries.map((e, i) => {
            const icons = { review:'📋', lesson:'💡', reflection:'🪞' };
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, animation:`ap-activity-in .3s ease ${i * 50 + 650}ms both` }}>
                <span style={{ fontSize:11 }}>{icons[e.type]}</span>
                <span style={{ fontSize:11, color:'rgba(168,159,140,.55)', flex:1 }}>{e.label}</span>
                <span style={{ fontSize:10, color:'rgba(168,159,140,.28)' }}>{e.time}</span>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}

/* ─── Account Comparison Table ─── */
function AccountComparison({ accounts, activeId, weekPnlMap = {} }) {
  if (accounts.length < 2) return null;
  return (
    <div style={{ background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.06)', borderRadius:11, overflow:'hidden', animation:'ap-fade-up .4s ease .4s both' }}>
      {/* Header */}
      <div style={{ padding:'13px 16px 11px', borderBottom:'1px solid rgba(255,255,255,.05)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:10, fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color:'rgba(168,159,140,.55)' }}>Account Comparison</span>
        <span style={{ fontSize:10, color:'rgba(168,159,140,.3)', fontFamily:"'IBM Plex Mono',monospace" }}>{accounts.length} accts</span>
      </div>

      {/* Column headers */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 72px 80px 80px', gap:8, padding:'7px 16px 6px', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
        {['Account','Balance','Wk Goal','Progress'].map(h => (
          <span key={h} style={{ fontSize:9, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'rgba(168,159,140,.3)', fontFamily:"'IBM Plex Mono',monospace" }}>{h}</span>
        ))}
      </div>

      {/* Rows */}
      {accounts.map((acct, i) => {
        const isActive = acct.id === activeId;
        const balance  = parseFloat(acct.balance) || 0;
        const weekPct  = parseFloat(acct.weekPct)  || 0;
        const target   = balance * weekPct / 100;
        const realPnl  = weekPnlMap[acct.id] ?? 0;
        const progress = target > 0 ? Math.min(100, (realPnl / target) * 100) : 0;
        const meta     = ACCOUNT_TYPE_META[acct.type] || ACCOUNT_TYPE_META['Live'];
        const barColor = progress >= 60 ? 'var(--green)' : progress >= 30 ? 'var(--gold)' : 'rgba(91,143,232,.8)';

        return (
          <div key={acct.id} className="ap-compare-row" style={{
            display:'grid', gridTemplateColumns:'1fr 72px 80px 80px', gap:8,
            padding:'9px 16px',
            background: isActive ? 'rgba(212,168,67,.03)' : 'transparent',
            borderBottom: i < accounts.length - 1 ? '1px solid rgba(255,255,255,.03)' : 'none',
            borderLeft: isActive ? '2px solid rgba(212,168,67,.35)' : '2px solid transparent',
            animation:`ap-activity-in .3s ease ${i * 60 + 420}ms both`,
          }}>
            {/* Name + type */}
            <div style={{ display:'flex', alignItems:'center', gap:7, minWidth:0 }}>
              <span style={{ fontSize:9, fontWeight:700, color:meta.color, background:meta.bg, border:`1px solid ${meta.border}`, padding:'1px 5px', borderRadius:3, letterSpacing:'.05em', fontFamily:"'IBM Plex Mono',monospace", flexShrink:0 }}>
                {meta.label}
              </span>
              <span style={{ fontSize:12, fontWeight:600, color: isActive ? 'var(--gold)' : 'var(--white)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {acct.name}
              </span>
              {isActive && <span style={{ width:5, height:5, borderRadius:'50%', background:'var(--gold)', boxShadow:'0 0 4px var(--gold)', flexShrink:0 }} />}
            </div>

            {/* Balance */}
            <span className="ap-mono" style={{ fontSize:12, fontWeight:600, color:'var(--white)', alignSelf:'center' }}>
              {balance > 0 ? fmtShort(balance) : '—'}
            </span>

            {/* Weekly goal $ */}
            <span className="ap-mono" style={{ fontSize:11, fontWeight:500, color:'rgba(168,159,140,.55)', alignSelf:'center' }}>
              {target > 0 ? fmtShort(target) : '—'}
            </span>

            {/* Progress mini-bar */}
            <div style={{ display:'flex', alignItems:'center', gap:6, alignSelf:'center' }}>
              <div style={{ flex:1, height:4, background:'rgba(255,255,255,.06)', borderRadius:2, overflow:'hidden' }}>
                <div className="ap-progress-fill" style={{ height:'100%', width:`${progress}%`, background:barColor, borderRadius:2 }} />
              </div>
              <span className="ap-mono" style={{ fontSize:10, fontWeight:600, color:barColor, minWidth:26, textAlign:'right' }}>
                {progress > 0 ? `${progress.toFixed(0)}%` : '—'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── MT Account auto-sync — Premium, coming soon ───
   Visual language matches: PricingPage's featured-plan badge/CTA tokens,
   and this file's own active/premium AccountCard treatment. ─── */
function MtSyncBanner({ setPage, isMobile }) {
  return (
    <div
      className="ap-account-card ap-account-card--active"
      style={{
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 14,
        padding: isMobile ? '18px' : '16px 20px',
        marginBottom: 20,
        background: 'linear-gradient(145deg, rgba(212,168,67,.065) 0%, rgba(14,14,12,1) 55%)',
        border: '1.5px solid rgba(212,168,67,.38)',
        borderRadius: 13,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top accent line — same treatment as featured plan card / active account card */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: 'linear-gradient(90deg, transparent 0%, rgba(212,168,67,.9) 30%, rgba(212,168,67,.9) 70%, transparent 100%)',
      }}/>
      {/* Premium corner ornament — same as active AccountCard */}
      <div style={{ position:'absolute', top:8, right:8, width:60, height:60, background:'radial-gradient(circle at top right, rgba(212,168,67,.1) 0%, transparent 65%)', pointerEvents:'none', borderRadius:'0 12px 0 0' }} />

      <div style={{ display:'flex', alignItems:'center', gap:12, flex: isMobile ? '1 1 auto' : '0 1 480px', minWidth:0 }}>
        <div style={{
          width: 36, height: 36, flexShrink: 0, borderRadius: 9,
          background: 'rgba(212,168,67,.1)', border: '1px solid rgba(212,168,67,.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:5 }}>
            <span style={{ fontSize:14, fontWeight:600, color:'var(--white)', letterSpacing:'-.01em' }}>
              Connect MT4 / MT5 account
            </span>
            {/* Badge — exact token match to PricingPage's plan badge (badgeStyle) */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
              padding: '3px 9px', borderRadius: 4,
              color: 'var(--gold)', background: 'rgba(212,168,67,.10)', border: '1px solid rgba(212,168,67,.26)',
              fontFamily: "'IBM Plex Mono',monospace",
            }}>
              Pro · Coming soon
            </span>
          </div>
          <div style={{ fontSize:11.5, color:'rgba(168,159,140,.55)', lineHeight:1.5 }}>
            Auto-import closed trades straight from your broker — no more manual logging.
          </div>
        </div>
      </div>

      {/* Trust signals — fills the space between description and CTA on wide screens
          instead of leaving it empty, and doubles as a preview of how the connection works */}
      {!isMobile && (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, minWidth:0 }}>
          <div style={{ width:1, height:22, background:'rgba(255,255,255,.07)', flexShrink:0 }} />
          {['Read-only access', 'No trading permissions', 'MT4 & MT5 supported'].map(t => (
            <span key={t} style={{
              display:'inline-flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
              fontSize:10.5, color:'rgba(168,159,140,.5)',
            }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="rgba(212,168,67,.6)" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
              {t}
            </span>
          ))}
        </div>
      )}

      {/* CTA — exact gradient/shadow token match to PricingPage's featured-plan button */}
      <button
        onClick={() => setPage && setPage('pricing')}
        className="ap-btn"
        style={{
          flexShrink: 0, width: isMobile ? '100%' : 'auto',
          display:'flex', alignItems:'center', justifyContent:'center', gap:7,
          padding: '12px 20px', borderRadius: 8,
          fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
          cursor: 'pointer', border: 'none',
          background: 'linear-gradient(135deg,#D4B060 0%,#C9A84C 60%,#B8963D 100%)',
          color: '#080808',
          boxShadow: '0 5px 18px rgba(201,168,76,.22)',
        }}
        onMouseEnter={e => { e.currentTarget.style.filter='brightness(1.06)'; }}
        onMouseLeave={e => { e.currentTarget.style.filter='none'; }}
      >
        Notify me at launch
      </button>
    </div>
  );
}

/* ─── Account rail item — compact, for the right-hand accounts list ─── */
function AccountRailItem({ acct, isActive, onSetActive, onEdit, onDelete, weekPnl = null }) {
  const meta = ACCOUNT_TYPE_META[acct.type] || ACCOUNT_TYPE_META['Live'];
  const balance = parseFloat(acct.balance) || 0;

  return (
    <div
      className="ap-rail-item"
      onClick={() => !isActive && onSetActive(acct.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 11px', borderRadius: 10,
        cursor: isActive ? 'default' : 'pointer',
        background: isActive ? 'rgba(212,168,67,.07)' : 'rgba(255,255,255,.02)',
        border: isActive ? '1px solid rgba(212,168,67,.32)' : '1px solid rgba(255,255,255,.06)',
      }}
    >
      {/* Type monogram */}
      <div style={{
        width: 30, height: 30, flexShrink: 0, borderRadius: 7,
        background: meta.bg, border: `1px solid ${meta.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 600, color: meta.color,
      }}>
        {meta.label}
      </div>

      {/* Name + balance */}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 12.5, fontWeight: 600, color: isActive ? 'var(--gold)' : 'var(--white)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {acct.name}
          </span>
          {isActive && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold)', boxShadow: '0 0 4px var(--gold)', flexShrink: 0 }} className="ap-live-dot" />}
        </div>
        <div className="ap-mono" style={{ fontSize: 11, color: 'rgba(168,159,140,.5)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {balance > 0 ? fmtShort(balance) : '—'}
          {weekPnl > 0 && <span style={{ color: 'var(--green)' }}>+{fmtShort(weekPnl)}</span>}
        </div>
      </div>

      {/* Edit / delete — reveal on hover */}
      <div className="ap-rail-actions" style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <button onClick={e => { e.stopPropagation(); onEdit(acct); }} className="ap-btn"
          style={{ width: 22, height: 22, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', color: 'rgba(168,159,140,.5)', borderRadius: 5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          title="Edit account">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
        </button>
        <button onClick={e => { e.stopPropagation(); onDelete(acct.id); }} className="ap-btn"
          style={{ width: 22, height: 22, background: 'transparent', border: '1px solid rgba(224,85,85,.12)', color: 'rgba(224,85,85,.35)', borderRadius: 5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          title="Delete account">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
        </button>
      </div>
    </div>
  );
}

/* ─── Empty state ─── */
function EmptyState({ onAdd }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'72px 32px', textAlign:'center', animation:'ap-fade-up .4s ease' }}>
      <div style={{ position:'relative', width:72, height:72, marginBottom:22 }}>
        <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:'1px solid rgba(212,168,67,.1)', animation:'ap-pulse-gold 3s ease infinite' }} />
        <div style={{ position:'absolute', inset:7, borderRadius:'50%', border:'1px solid rgba(212,168,67,.16)' }} />
        <div style={{ position:'absolute', inset:15, borderRadius:'50%', background:'rgba(212,168,67,.07)', border:'1px solid rgba(212,168,67,.18)', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
        </div>
      </div>
      <div style={{ fontSize:17, fontWeight:700, color:'var(--white)', marginBottom:8, letterSpacing:'-.02em' }}>No accounts yet</div>
      <div style={{ fontSize:12, color:'rgba(168,159,140,.45)', marginBottom:28, maxWidth:300, lineHeight:1.7 }}>
        Add your live, demo, or prop firm accounts. Each tracks its own balance, risk rules, and trade journal entries.
      </div>
      <div style={{ display:'flex', gap:7, flexWrap:'wrap', justifyContent:'center', marginBottom:28 }}>
        {Object.entries(ACCOUNT_TYPE_META).map(([type, m]) => (
          <div key={type} style={{ padding:'4px 12px', background:m.bg, border:`1px solid ${m.border}`, borderRadius:20, fontSize:10, fontWeight:700, color:m.color, letterSpacing:'.06em', fontFamily:"'IBM Plex Mono',monospace" }}>
            {m.label}
          </div>
        ))}
      </div>
      <button onClick={onAdd} className="ap-btn"
        style={{ padding:'12px 28px', fontSize:12, fontWeight:700, background:'linear-gradient(135deg,var(--gold),var(--gold-dim))', border:'none', color:'var(--black)', cursor:'pointer', borderRadius:9, letterSpacing:'.06em', boxShadow:'0 6px 24px rgba(212,168,67,.3)', display:'flex', alignItems:'center', gap:8, transition:'all .15s' }}
        onMouseEnter={e => { e.currentTarget.style.filter='brightness(1.08)'; e.currentTarget.style.transform='translateY(-2px)'; }}
        onMouseLeave={e => { e.currentTarget.style.filter='none'; e.currentTarget.style.transform='none'; }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        ADD YOUR FIRST ACCOUNT
      </button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   DASHBOARD SUMMARY PANEL — Premium v2
════════════════════════════════════════════════════════════ */
function DashboardSummary({ accounts, activeId, stats, equityPoints }) {
  const activeAcct    = accounts.find(a => a.id === activeId);
  const totalBalance  = accounts.reduce((s, a) => s + (parseFloat(a.balance) || 0), 0);
  const activeBalance = parseFloat(activeAcct?.balance) || 0;
  const activeMeta    = ACCOUNT_TYPE_META[activeAcct?.type] || ACCOUNT_TYPE_META['Live'];

  // Real stats from API — fall back to null/0 when no trades exist yet
  const weeklyPnl    = stats?.weeklyPnl    ?? null;
  const winRate      = stats?.winRate      ?? null;
  const profitFactor = stats?.profitFactor ?? null;
  const tradesCount  = stats?.tradesCount  ?? 0;
  const avgRR        = stats?.avgRR        ?? null;
  const weekPct      = parseFloat(activeAcct?.weekPct) || 0;
  const weekTarget   = activeBalance * weekPct / 100;

  if (accounts.length === 0) return null;

  return (
    <div style={{ marginBottom:24, animation:'ap-fade-up .35s ease' }}>

      {/* ── PREMIUM ACTIVE ACCOUNT HERO ── */}
      {activeAcct && (
        <div style={{
          position:'relative', overflow:'hidden',
          marginBottom:14, borderRadius:13,
          border:'1.5px solid rgba(212,168,67,.32)',
          background:'linear-gradient(135deg, rgba(212,168,67,.07) 0%, rgba(212,168,67,.03) 40%, rgba(14,14,12,.98) 100%)',
          boxShadow:'0 0 0 1px rgba(212,168,67,.08), 0 12px 40px rgba(212,168,67,.09), 0 4px 16px rgba(0,0,0,.4)',
        }}>
          {/* Decorative corner glow */}
          <div style={{ position:'absolute', top:-30, left:-30, width:120, height:120, background:'radial-gradient(circle, rgba(212,168,67,.12) 0%, transparent 70%)', pointerEvents:'none' }} />
          <div style={{ position:'absolute', bottom:-20, right:60, width:80, height:80, background:'radial-gradient(circle, rgba(212,168,67,.06) 0%, transparent 70%)', pointerEvents:'none' }} />

          <div style={{ position:'relative', padding:'16px 20px' }}>
            {/* Badge row */}
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' }}>
              <div style={{
                display:'inline-flex', alignItems:'center', gap:5,
                padding:'3px 10px 3px 8px',
                background:'rgba(212,168,67,.12)',
                border:'1px solid rgba(212,168,67,.28)',
                borderRadius:20,
              }} className="ap-active-badge">
                <span style={{ width:5, height:5, borderRadius:'50%', background:'var(--gold)', boxShadow:'0 0 5px var(--gold)', flexShrink:0 }} className="ap-live-dot" />
                <span style={{ fontSize:9, fontWeight:800, color:'var(--gold)', letterSpacing:'.1em', fontFamily:"'IBM Plex Mono',monospace" }}>CURRENT TRADING ACCOUNT</span>
              </div>
              <span style={{ fontSize:10, fontWeight:700, color:activeMeta.color, background:activeMeta.bg, border:`1px solid ${activeMeta.border}`, padding:'2px 8px', borderRadius:4, letterSpacing:'.06em', fontFamily:"'IBM Plex Mono',monospace" }}>
                {activeMeta.label}
              </span>
              {activeAcct.propFirm && (
                <span style={{ fontSize:10, color:'rgba(168,159,140,.5)', fontFamily:"'IBM Plex Mono',monospace" }}>{activeAcct.propFirm}</span>
              )}
            </div>

            {/* Main row: name + balance + equity + week */}
            <div style={{ display:'flex', alignItems:'flex-start', gap:20, flexWrap:'wrap' }}>
              {/* Left: name + broker */}
              <div style={{ flex:'1 1 160px', minWidth:0 }}>
                <div style={{ fontSize:18, fontWeight:700, color:'var(--white)', letterSpacing:'-.02em', marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {activeAcct.name}
                </div>
                {activeAcct.broker && (
                  <div style={{ fontSize:11, color:'rgba(168,159,140,.45)' }}>{activeAcct.broker}</div>
                )}
              </div>

              {/* Balance */}
              {activeBalance > 0 && (
                <div style={{ flexShrink:0 }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color:'rgba(168,159,140,.4)', marginBottom:4 }}>Balance</div>
                  <div className="ap-mono" style={{ fontSize:26, fontWeight:600, color:'var(--gold)', letterSpacing:'-.03em', lineHeight:1 }}>
                    {fmtShort(activeBalance)}
                  </div>
                </div>
              )}

              {/* Divider */}
              {activeBalance > 0 && <div style={{ width:1, alignSelf:'stretch', background:'rgba(212,168,67,.12)', flexShrink:0, margin:'4px 0' }} />}

              {/* This week P&L */}
              {activeBalance > 0 && (
                <div style={{ flexShrink:0 }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color:'rgba(168,159,140,.4)', marginBottom:4 }}>This Week</div>
                  {weeklyPnl === null ? (
                    <div className="ap-mono" style={{ fontSize:18, fontWeight:600, color:'rgba(168,159,140,.35)', letterSpacing:'-.02em', lineHeight:1 }}>No data</div>
                  ) : (
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <div className="ap-mono" style={{ fontSize:18, fontWeight:600, color: weeklyPnl >= 0 ? 'var(--green)' : 'var(--red)', letterSpacing:'-.02em', lineHeight:1 }}>
                        {weeklyPnl >= 0 ? '+' : ''}{fmtShort(weeklyPnl)}
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize:10, color:'rgba(168,159,140,.38)', marginTop:2 }}>vs last week</div>
                </div>
              )}

              {/* Equity curve */}
              {activeBalance > 0 && (
                <div style={{ flexShrink:0, alignSelf:'center' }}>
                  <EquityCurve color="var(--gold)" width={96} height={34} points={equityPoints} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── KPI GRID — 4 cards. "Balance" and "This week" already live in the
           hero above, so this row only shows figures the hero doesn't. ── */}
      <div className="ap-kpi-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:12 }}>
        <KpiCard
          label="Total Balance"
          value={fmtShort(totalBalance)}
          sub={`Across ${accounts.length} account${accounts.length !== 1 ? 's' : ''}`}
          accent="var(--gold)"
          delay={0}
        />
        <KpiCard
          label="Win Rate"
          value={winRate === null ? '—' : `${winRate.toFixed(1)}%`}
          sub={winRate === null ? 'No trades yet' : 'Last 30 trades'}
          delay={40}
        />
        <KpiCard
          label="Profit Factor"
          value={profitFactor === null ? '—' : profitFactor.toFixed(2)}
          sub={tradesCount < 5 ? 'Needs 5+ trades' : 'Wins vs losses'}
          delay={80}
        />
        <KpiCard
          label="Trades Logged"
          value={tradesCount || '—'}
          sub="All time"
          delay={120}
        />
      </div>

      {/* ── WEEKLY GOAL + BOTTOM PANELS ── */}
      <div style={{ display:'grid', gridTemplateColumns: weekTarget > 0 ? '1fr 1fr' : '1fr', gap:10, marginBottom:14 }} className="ap-insight-row">
        {/* Weekly target progress */}
        {weekTarget > 0 && (
          <div style={{ padding:'16px 18px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.06)', borderRadius:11, animation:'ap-fade-up .4s ease .22s both' }}>
            <WeeklyProgress current={weeklyPnl ?? 0} target={weekTarget} label="Weekly Goal Progress" />
          </div>
        )}

        {/* Quick stats row */}
        <div style={{ padding:'14px 18px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.06)', borderRadius:11, animation:'ap-fade-up .4s ease .28s both', display:'flex', flexDirection:'column', gap:10 }}>
          <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color:'rgba(168,159,140,.4)' }}>Session Stats</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {[
              { label:'Trades Today', value: tradesCount > 0 ? String(tradesCount) : '0', color:'var(--white)' },
              { label:'Avg R:R',      value: avgRR !== null ? avgRR.toFixed(1) : '—',  color:'var(--green)' },
              { label:'Daily Limit',  value: activeAcct?.dailyPct ? `${activeAcct.dailyPct}%` : '—', color:'var(--red)' },
              { label:'Max/Day',      value: activeAcct?.maxTrades || '∞', color:'rgba(168,159,140,.7)' },
            ].map(s => (
              <div key={s.label}>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'rgba(168,159,140,.35)', marginBottom:3 }}>{s.label}</div>
                <div className="ap-mono" style={{ fontSize:14, fontWeight:600, color:s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════ */
export default function AccountsPage({ setPage } = {}) {
  const w = useWindowWidth();
  const isMobile = w < 640;

  const [accounts,    setAccounts]    = useState([]);
  const [activeId,    setActiveId]    = useState(null);
  const [showDrawer,  setShowDrawer]  = useState(false);
  const [editingAcct, setEditingAcct] = useState(null);
  const [confirm,     setConfirm]     = useState(null);
  const [toast,       setToast]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [stats,       setStats]       = useState(null);
  const [recentTrades,setRecentTrades]= useState([]);
  const [equityPoints,setEquityPoints]= useState([]);
  const [weekPnlMap,  setWeekPnlMap]  = useState({});
  const [userId,      setUserId]      = useState(null);
  const [profileMeta, setProfileMeta] = useState(null); // holds required PUT fields

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── Load accounts ── */
  useEffect(() => {
    api('/profile')
      .then(data => {
        const uid = data.user?.id || null;
        setUserId(uid);
        // Store the fields the PUT schema requires so persist can include them
        if (data.profile) {
          setProfileMeta({
            name:     data.user?.name     || '',
            level:    data.profile.level  || 'Level 1 Student',
            timezone: data.profile.timezone || 'UTC',
            broker:   data.profile.broker  ?? null,
            accType:  data.profile.accType || 'Demo',
          });
        }
        const accts = Array.isArray(data.profile?.tradingAccounts) ? data.profile.tradingAccounts : [];
        if (accts.length) {
          setAccounts(accts);
          // Same priority as App.jsx: localStorage > server
          const persistedId = uid ? localStorage.getItem(`kfx:active_account:${uid}`) : null;
          const resolvedId = (persistedId && accts.find(a => a.id === persistedId))
            ? persistedId
            : (data.profile.activeAccountId || null);
          setActiveId(resolvedId);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /* ── Load real trade stats whenever activeId changes ── */
  useEffect(() => {
    if (!activeId) return;
    // Fetch stats for the active account
    tradesApi.stats(activeId)
      .then(s => setStats(s))
      .catch(() => setStats(null));

    // Fetch recent trades for the active account (last 7 days / first page)
    api(`/trades?page=1&limit=10&accountId=${encodeURIComponent(activeId)}`)
      .then(data => {
        const trades = (data.trades ?? []);
        setRecentTrades(trades);
        // Build cumulative equity curve from trade P&L
        let cum = 0;
        const pts = trades
          .slice()
          .reverse()
          .map(t => { cum += (parseFloat(t.pnl) || 0); return cum; });
        setEquityPoints(pts.length ? pts : []);
      })
      .catch(() => { setRecentTrades([]); setEquityPoints([]); });
  }, [activeId]);

  /* ── Build weekPnlMap for AccountComparison (all accounts) ── */
  useEffect(() => {
    if (accounts.length < 2) return;
    Promise.all(
      accounts.map(a =>
        tradesApi.stats(a.id)
          .then(s => ({ id: a.id, pnl: s?.weeklyPnl ?? 0 }))
          .catch(() => ({ id: a.id, pnl: 0 }))
      )
    ).then(results => {
      const map = {};
      results.forEach(r => { map[r.id] = r.pnl; });
      setWeekPnlMap(map);
    });
  }, [accounts]);

  /* ── Sidebar event hook ── */
  useEffect(() => {
    const handler = () => { setEditingAcct(null); setShowDrawer(true); };
    window.addEventListener('kfx:open-add-account', handler);
    return () => window.removeEventListener('kfx:open-add-account', handler);
  }, []);

  /* ── Handlers ── */
  const persist = async (accts, aId) => {
    try {
      // UpdateProfileSchema requires name/level/timezone/accType — merge with stored values
      const base = profileMeta || { name: '', level: 'Level 1 Student', timezone: 'UTC', accType: 'Demo' };
      await api('/profile', {
        method: 'PUT',
        body: JSON.stringify({
          ...base,
          tradingAccounts: accts,
          activeAccountId: aId,
        }),
      });
      // Keep localStorage in sync so App.jsx resolves the correct active account on refresh
      if (userId && aId) {
        try { localStorage.setItem(`kfx:active_account:${userId}`, aId); } catch (_) {}
      }
      // Keep sessionStorage in sync so TradeModal self-resolves
      if (userId) {
        try {
          sessionStorage.setItem(`kfx:accounts:${userId}`, JSON.stringify({
            tradingAccounts: accts,
            activeAccountId: aId,
          }));
        } catch (_) {}
      }
      // Notify App.jsx + Topbar + TradeModal
      window.dispatchEvent(new CustomEvent('kfx:accounts-updated', {
        detail: { tradingAccounts: accts, activeAccountId: aId },
      }));
    } catch (err) {
      const msg = err?.message || 'Failed to save — please try again';
      showToast(`Save failed: ${msg}`, 'error');
      console.error('[AccountsPage] persist failed:', err);
      throw err;
    }
  };

  const handleAdd = async (acct) => {
    const prevAccounts = accounts;
    const prevActiveId = activeId;
    const updated = [...accounts, acct];
    const newActiveId = accounts.length === 0 ? acct.id : activeId;
    setAccounts(updated);
    setActiveId(newActiveId);
    setShowDrawer(false);
    try {
      await persist(updated, newActiveId);
      showToast(`"${acct.name}" added`);
    } catch {
      // Roll back optimistic update
      setAccounts(prevAccounts);
      setActiveId(prevActiveId);
    }
  };

  const handleEdit = async (acct) => {
    const prev = accounts;
    const updated = accounts.map(a => a.id === acct.id ? acct : a);
    setAccounts(updated);
    setEditingAcct(null);
    setShowDrawer(false);
    try {
      await persist(updated, activeId);
      showToast(`"${acct.name}" updated`);
    } catch {
      setAccounts(prev);
    }
  };

  const handleSetActive = async (id) => {
    setActiveId(id);
    await persist(accounts, id);
    const acct = accounts.find(a => a.id === id);
    showToast(`Switched to "${acct?.name}"`);
  };

  const handleDelete = (id) => {
    const acct = accounts.find(a => a.id === id);
    setConfirm({
      title: `Delete "${acct?.name}"?`,
      body: 'This account and its risk settings will be removed. Your trade journal data is not affected.',
      confirmLabel: 'Delete Account',
      danger: true,
      onConfirm: async () => {
        const updated = accounts.filter(a => a.id !== id);
        const newActiveId = activeId === id ? (updated[0]?.id || null) : activeId;
        setAccounts(updated);
        setActiveId(newActiveId);
        setConfirm(null);
        await persist(updated, newActiveId);
        showToast('Account deleted');
      },
    });
  };

  const openAdd  = () => { setEditingAcct(null); setShowDrawer(true); };
  const openEdit = (a) => { setEditingAcct(a); setShowDrawer(true); };
  const handleSave = (acct) => acct.id && accounts.find(a => a.id === acct.id) ? handleEdit(acct) : handleAdd(acct);

  /* ── Page padding ── */
  const pad = isMobile ? '18px 16px' : '28px 32px';
  const maxW = 1280;

  return (
    <>
      <style>{PAGE_STYLES}</style>
      <div className="ap-root ap-scroll" style={{ flex:1, overflowY:'auto', overflowX:'hidden' }}>

        {/* ── Page header ── */}
        <div style={{ padding: isMobile ? '18px 16px 0' : '28px 32px 0', maxWidth:maxW, width:'100%' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:20, flexWrap:'wrap', animation:'ap-fade-up .28s ease' }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:3 }}>
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.1em', color:'rgba(168,159,140,.45)', textTransform:'uppercase', fontFamily:"'IBM Plex Mono',monospace" }}>
                  Trading Journal
                </div>
                <span style={{ width:1, height:11, background:'rgba(168,159,140,.2)' }} />
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.1em', color:'var(--gold)', textTransform:'uppercase', fontFamily:"'IBM Plex Mono',monospace" }}>
                  Accounts
                </div>
              </div>
              <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight:700, color:'var(--white)', letterSpacing:'-.03em', margin:0, lineHeight:1.1 }}>
                My Accounts
              </h1>
              {accounts.length > 0 && (
                <div style={{ fontSize:11, color:'rgba(168,159,140,.4)', marginTop:4 }}>
                  {accounts.length} account{accounts.length !== 1 ? 's' : ''} configured
                </div>
              )}
            </div>

            {accounts.length < 10 && (
              <button onClick={openAdd} className="ap-btn"
                style={{ padding:'9px 18px', fontSize:11, fontWeight:700, background:'linear-gradient(135deg,var(--gold),var(--gold-dim))', border:'none', color:'var(--black)', cursor:'pointer', borderRadius:8, letterSpacing:'.06em', boxShadow:'0 4px 16px rgba(212,168,67,.22)', display:'flex', alignItems:'center', gap:6, flexShrink:0, animation:'ap-fade-in .4s ease', width: isMobile ? '100%' : 'auto', justifyContent:'center', minHeight:38, position:'relative', overflow:'hidden', transition:'filter .15s' }}
                onMouseEnter={e => e.currentTarget.style.filter='brightness(1.08)'}
                onMouseLeave={e => e.currentTarget.style.filter='none'}>
                <span style={{ position:'absolute', inset:0, background:'linear-gradient(90deg,transparent 20%,rgba(255,255,255,.16) 50%,transparent 80%)', animation:'ap-shimmer 2.8s ease infinite', pointerEvents:'none' }} />
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                ADD ACCOUNT
              </button>
            )}
          </div>

          <MtSyncBanner setPage={setPage} isMobile={isMobile} />
        </div>

        {/* ── Content ── */}
        <div style={{ padding: isMobile ? '0 16px 80px' : '0 32px 52px', maxWidth:maxW, width:'100%' }}>

          {loading ? (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'90px 0' }}>
              <div style={{ width:26, height:26, border:'2px solid rgba(212,168,67,.2)', borderTopColor:'var(--gold)', borderRadius:'50%', animation:'ap-spin .7s linear infinite' }} />
            </div>
          ) : accounts.length === 0 ? (
            <EmptyState onAdd={openAdd} />
          ) : (
            <div className="ap-page-grid">

              {/* ── Left: main content ── */}
              <div style={{ minWidth:0 }}>
                <DashboardSummary accounts={accounts} activeId={activeId} stats={stats} equityPoints={equityPoints} />

                <div className="ap-bottom-row" style={{ display:'grid', gridTemplateColumns: accounts.length >= 2 ? '1fr 1fr' : '1fr', gap:12, marginBottom:18 }}>
                  <RecentActivity trades={recentTrades} />
                  {accounts.length >= 2 && <AccountComparison accounts={accounts} activeId={activeId} weekPnlMap={weekPnlMap} />}
                </div>

                <div style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'11px 14px', background:'rgba(91,143,232,.04)', border:'1px solid rgba(91,143,232,.1)', borderRadius:9 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" style={{ flexShrink:0, marginTop:1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span style={{ fontSize:11, color:'rgba(168,159,140,.5)', lineHeight:1.7 }}>
                    <strong style={{ color:'var(--blue)', fontWeight:700 }}>Tip:</strong> The active account's balance and risk rules apply when you log a new trade. Switch accounts instantly to journal prop firm, live, and demo setups separately.
                  </span>
                </div>
              </div>

              {/* ── Right: accounts rail — persistent switcher, no scrolling required ── */}
              <div className="ap-rail">
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                  <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'rgba(168,159,140,.45)', fontFamily:"'IBM Plex Mono',monospace" }}>
                    All Accounts
                  </div>
                  <div style={{ flex:1, height:1, background:'rgba(255,255,255,.05)' }} />
                  <div style={{ fontSize:10, color:'rgba(168,159,140,.35)', fontFamily:"'IBM Plex Mono',monospace" }}>
                    {accounts.length} / {10} used
                  </div>
                </div>

                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {accounts.map((acct, i) => (
                    <div key={acct.id} style={{ animation:`ap-fade-up .24s ease ${i * 45}ms both` }}>
                      <AccountRailItem
                        acct={acct}
                        isActive={acct.id === activeId}
                        onSetActive={handleSetActive}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                        weekPnl={weekPnlMap[acct.id] ?? null}
                      />
                    </div>
                  ))}

                  {accounts.length < 10 && (
                    <button onClick={openAdd} className="ap-btn"
                      style={{
                        display:'flex', alignItems:'center', gap:9,
                        padding:'10px 11px', borderRadius:10, cursor:'pointer',
                        background:'transparent', border:'1px dashed rgba(212,168,67,.25)',
                        color:'rgba(212,168,67,.65)', fontSize:12, fontWeight:600,
                        transition:'border-color .15s, background .15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(212,168,67,.5)'; e.currentTarget.style.background='rgba(212,168,67,.04)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(212,168,67,.25)'; e.currentTarget.style.background='transparent'; }}>
                      <div style={{ width:30, height:30, flexShrink:0, borderRadius:7, border:'1px dashed rgba(212,168,67,.35)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      </div>
                      Add another account
                    </button>
                  )}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>

      {showDrawer && (
        <AccountDrawer
          acct={editingAcct}
          onSave={handleSave}
          onCancel={() => { setShowDrawer(false); setEditingAcct(null); }}
          isMobile={isMobile}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          body={confirm.body}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} />}
    </>
  );
}